<template>
    <page-loading v-if="loading"></page-loading>
    <form-container v-else>
        <step-form-heading v-if="!props.snippetId"
            :icon="faClipboard">
            Snippet Template

            <template v-slot:description>
                Create a Snippet and share it with Groups.
            </template>
        </step-form-heading>

        <page-header v-else
            class="vx-mb-6 lg:vx-mb-12">
            <template v-slot:subtitle>
                <a href="/messaging/snippets/templates"
                    class="!vx-no-underline !vx-text-sky-600 vx-text-base vx-font-normal">
                    <font-awesome-icon class="vx-mr-1"
                        :icon="faArrowLeft"></font-awesome-icon>
                    All Snippet Templates
                </a>
            </template>

            {{ snippet.title }}
        </page-header>

        <div class="vx-grid vx-grid-cols-12">
            <page-sidebar v-if="props.snippetId"
                :links="[
                    { href: `/messaging/snippets/templates/${props.snippetId}`, label: 'Details' },
                ]">
            </page-sidebar>

            <vx-sticky-nav
                :sections="[
                    { id: 'title', label: 'Name' },
                    { id: 'description', label: 'Description' },
                    { id: 'snippet', label: 'Snippet' },
                    { id: 'groups', label: 'Groups' },
                ]"
                class="vx-col-span-12"
                :class="{
                    'lg:vx-col-span-10': props.snippetId,
                }"
                :navClasses="`${props.snippetId ? 'vx-hidden' : ''}`">

                <div class="vx-flex vx-flex-col vx-w-full vx-justify-center vx-gap-2 md:vx-gap-8">

                    <vx-alert v-if="success"
                        class="vx-max-w-screen-sm"
                        :class="{
                            'vx-mx-auto': !props.snippetId,
                        }"
                        color="success"
                        :closeable="false"
                        shadow>
                        <template v-slot:icon>
                            <font-awesome-icon :icon="faCircleCheck"></font-awesome-icon>
                        </template>
                        <div class="vx-flex vx-items-center vx-justify-between">
                            <span>
                                <strong>{{ props.snippetId ? 'Existing Snippet' : 'New Snippet' }}</strong>
                                {{ props.snippetId ? 'updated' : 'created' }}
                                successfully
                            </span>
                            <vx-button href="/messaging/snippets/templates"
                                size="sm"
                                color="success">Back to Snippets<font-awesome-icon
                                    :icon="faSquareArrowUpLeft"></font-awesome-icon></vx-button>
                        </div>
                    </vx-alert>

                    <vx-expandable-card id="title"
                        class="vx-max-w-screen-sm"
                        :class="{
                            'vx-mx-auto': !props.snippetId,
                        }">
                        <template v-slot:header> Name </template>

                        <template v-slot:content>
                            <vx-input v-model="snippet.title"
                                data-test="title"
                                :error="errors.title || displayFirstError(validate.title.$errors)"
                                @blur="validate.title.$touch()"
                                autofocus
                                size="lg"
                                :maxlength="120"></vx-input>
                        </template>
                    </vx-expandable-card>

                    <vx-expandable-card id="description"
                        class="vx-max-w-screen-sm"
                        :class="{
                            'vx-mx-auto': !props.snippetId,
                        }">
                        <template v-slot:header> Description </template>

                        <template v-slot:content>
                            <vx-input v-model="snippet.description"
                                data-test="description"
                                :error="errors.description || displayFirstError(validate.description.$errors)"
                                @blur="validate.description.$touch()"
                                autofocus
                                autoresize
                                :rows="3"
                                size="lg"
                                :maxlength="2000"></vx-input>
                        </template>
                    </vx-expandable-card>

                    <vx-expandable-card id="snippet"
                        class="vx-max-w-screen-sm"
                        :class="{
                            'vx-mx-auto': !props.snippetId,
                        }">
                        <template v-slot:header> Snippet </template>
                        <template v-slot:content>
                            <vx-label>Message</vx-label>
                            <message-builder ref="messageBuilderRef"
                                v-model="snippet.body"
                                v-model:mediaUrl="snippet.media_url"
                                v-model:mediaContentType="snippet.media_content_type"
                                disableSnippets
                                testable
                                :error="errors.body || displayFirstError(validate.body)"></message-builder>
                        </template>
                    </vx-expandable-card>


                    <snippet-template-form-group-access id="groups" :snippetId="props.snippetId ? Number(props.snippetId) : null"
                        v-model:groupFilters="snippet.group_filters"
                        v-model:groupCollectionId="snippet.group_collection_id"
                        :class="{
                            'vx-mx-auto vx-max-w-5xl vx-w-full': !props.snippetId,
                            'vx-w-full xl:vx-w-[62rem]': props.snippetId
                        }">
                    </snippet-template-form-group-access>

                    <div v-if="error"
                        class="vx-max-w-screen-sm"
                        :class="{
                            'vx-mx-auto': !props.snippetId,
                        }">
                        <vx-alert color="danger"
                            :closeable="false">
                            {{ error }}
                        </vx-alert>
                    </div>

                    <vx-sticky-footer class="!vx-flex-col lg:vx-flex lg:!vx-flex-row vx-max-w-screen-sm"
                        :class="{
                            'vx-mx-auto': !props.snippetId,
                        }">
                        <vx-button href="/messaging/snippets/templates"
                            class="vx-order-3 lg:vx-w-20 lg:vx-order-1"
                            size="lg"
                            color="muted-dark">
                            Cancel
                        </vx-button>
                        <vx-button @click="submit()"
                            data-test="submit"
                            :loading="saving"
                            type="button"
                            class="vx-order-1 lg:vx-grow lg:vx-order-3"
                            size="lg"
                            color="primary">
                            Save
                        </vx-button>
                    </vx-sticky-footer>
                </div>
            </vx-sticky-nav>
        </div>
    </form-container>
</template>

<script setup>
import { faArrowLeft, faCircleCheck, faClipboard, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxLabel, VxButton, VxExpandableCard, VxInput, VxStickyFooter, VxStickyNav } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { maxLength, required } from '@vuelidate/validators';
import { ref } from 'vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageSidebar from '~/components/general/PageSidebar.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import FormContainer from '~/components/general/FormContainer.vue';
import { useToasted } from '~/composables/useToasted';
import { HTTP_INTERNAL_SERVER_ERROR, HTTP_UNPROCESSABLE_ENTITY } from '~/support/HttpStatuses';
import { displayFirstError } from '~/utils/validation';
import StepFormHeading from '~/components/general/StepFormHeading.vue';
import { snippetsClient } from '~/services';
import MessageBuilder from '~/components/general/message-builder/MessageBuilder.vue';
import SnippetTemplateFormGroupAccess from './SnippetTemplateFormGroupAccess.vue';

const teamId = Spark.state.currentTeam.id;
const toasted = useToasted();
const loading = ref(false);

const defaultData = () => {
    return {
        title: '',
        description: '',
        body: '',
        group_filters: [],
        group_collection_id: null,
    }
}

const snippet = ref(defaultData());

const props = defineProps({
    snippetId: {
        type: [Number, String],
    }
})

if (props.snippetId) {
    loading.value = true;
    snippetsClient.getSnippet(teamId, props.snippetId)
        .then(response => {
            snippet.value = response.data
            loading.value = false;
        });
}

const validate = useVuelidate({
    title: {
        required,
        maxLength: maxLength(120)
    },
    description: {
        required,
        maxLength: maxLength(2000)
    },
    body: {
        required,
        maxLength: maxLength(1600)
    }
}, snippet)

const success = ref(false);

const error = ref(undefined);
const errors = ref({});
const saving = ref(false);

const submit = async () => {
    const isValid = await validate.value.$validate();

    if (!isValid) {

        toasted.error('Please check form data.');
        return;
    }

    if (saving.value) {
        return;
    }

    try {
        saving.value = true;
        errors.value = {};

        snippet.value.is_template = true;
        if (props.snippetId) {
            await snippetsClient.updateSnippet(teamId, props.snippetId, snippet.value);
        } else {
            await snippetsClient.createSnippet(teamId, snippet.value);
        }

        window.scrollTo({ top: 0, behavior: 'smooth' });
        success.value = true;
        validate.value.$reset();
        if (!props.snippetId) {
            snippet.value = defaultData();
        }
    } catch (e) {
        console.error(e);

        if (!e?.response?.status || e.response.status >= HTTP_INTERNAL_SERVER_ERROR) {
            toasted.global.platform_error();
            return
        } else if (e.response.status === HTTP_UNPROCESSABLE_ENTITY) {
            errors.value = e.response.data.errors;
        } else {
            error.value = e.response?.data?.message || 'Something went wrong.';
        }

        toasted.error('Please check form data.');
    } finally {
        saving.value = false;
    }
}

</script>
